import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Typography,
    Grid,
    Stack,
    Chip,
    Link,
    Divider,
    IconButton,
} from "@mui/material";
import {
    Mail,
    Phone,
    MapPin,
    Linkedin,
    Award,
    Edit3,
} from "lucide-react";
import parse from "html-react-parser";
import { toTitleCase } from "../utils";
import { resumeBuilderSelector } from "../ResumeBuilderActions/selector";
import ResumeContext from "../Resumecontext";
import { setCurrentSection } from "../ResumeBuilderActions/actions";

const Template3 = ({ accentColor = "#1976d2", forPDF = false, addedSkills = [] }) => {
    const dispatch = useDispatch();
    const context = useContext(ResumeContext);
    const { currentSection, resumeFullDetails, projects } = useSelector(resumeBuilderSelector);

    const {
        resumedetails = {},
        projectdetails = [],
        educationdetails = [],
        certificatedetails = [],
    } = resumeFullDetails || {};

    const [displayedProjects, setDisplayedProjects] = useState([]);

    useEffect(() => {
        if (currentSection?.section === "Project") {
            setDisplayedProjects(projects);
        } else {
            setDisplayedProjects(projectdetails);
        }
    }, [currentSection, projects, projectdetails]);

    const handleEditClick = (section, id) => {
        dispatch(setCurrentSection(section, id));
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr + "-01");
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
        });
    };

    const skills = [
        ...new Set(
            [resumedetails?.ai_skills, context.aiSkills]
                .filter(Boolean)
                .join(", ")
                .split(",")
                .map((skill) => skill.trim())
                .filter(Boolean)
        ),
    ];

    return (
        <Box
            sx={{
                width: "100%",
                bgcolor: "#fff",
                color: "#1a1a1a",
                boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
                fontFamily: "'Outfit', sans-serif",
                minHeight: '92vh',
                display: "flex",
                flexDirection: "column"
            }}
        >
            {/* ===== MODERN HEADER ===== */}
            <Box sx={{
                bgcolor: accentColor,
                color: "#fff",
                p: { xs: 3, sm: 4 },
                position: "relative",
            }}>
                <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12} md={8}>
                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, textTransform: 'capitalize', letterSpacing: -0.5 }}>
                            {resumedetails.full_name || "Your Name"}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ opacity: 0.9, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase', fontSize: "0.9rem" }}>
                            {context.jobTitle || resumedetails.application_title || "Your Profession"}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Stack spacing={0.5} sx={{ fontSize: "0.85rem" }}>
                            {resumedetails.email && (
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ cursor: 'pointer' }} onClick={() =>
                                    window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=${resumedetails.email}`, "_blank")
                                }>
                                    <Mail size={14} />
                                    <Typography sx={{ fontWeight: 500, fontSize: "inherit" }}>{resumedetails.email.toLowerCase()}</Typography>
                                </Stack>
                            )}
                            {resumedetails.home_phone && (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Phone size={14} />
                                    <Typography sx={{ fontWeight: 500, fontSize: "inherit" }}>{resumedetails.home_phone}</Typography>
                                </Stack>
                            )}
                            {resumedetails.statename && (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <MapPin size={14} />
                                    <Typography sx={{ fontWeight: 500, fontSize: "inherit" }}>{`${resumedetails.cityname}, ${resumedetails.statename}`}</Typography>
                                </Stack>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
            </Box>

            {/* ===== CONTENT BODY ===== */}
            <Box sx={{ p: { xs: 3, sm: 4 }, flexGrow: 1 }}>

                {/* 1. Summary */}
                {(context.summary || resumedetails.summary) && (
                    <Box mb={3}>
                        <Typography variant="subtitle2" sx={{ color: accentColor, fontWeight: 800, mb: 1, textTransform: "uppercase", letterSpacing: 1.5, borderBottom: `1px solid ${accentColor}30`, pb: 0.5 }}>
                            Summary
                        </Typography>
                        <Typography sx={{ color: "#444", textAlign: forPDF ? "left" : "justify", lineHeight: 1.6, fontSize: "0.85rem" }}>
                            {parse(context.summary || resumedetails.summary || "")}
                        </Typography>
                    </Box>
                )}

                {/* 2. Technical Skills */}
                {skills.length > 0 && (
                    <Box mb={3}>
                        <Typography variant="subtitle2" sx={{ color: accentColor, fontWeight: 800, mb: 1.5, textTransform: "uppercase", letterSpacing: 1.5, borderBottom: `1px solid ${accentColor}30`, pb: 0.5 }}>
                            Technical Skills
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                            {skills.map((skill, index) => (
                                <Chip
                                    key={index}
                                    label={skill}
                                    sx={{
                                        bgcolor: (addedSkills?.includes(skill) && !forPDF) ? "yellow" : "transparent",
                                        color: (addedSkills?.includes(skill) && !forPDF) ? "#000" : "#444",
                                        border: `1px solid ${accentColor}40`,
                                        fontWeight: 600,
                                        px: 0.5,
                                        py: 0.2,
                                        fontSize: "0.75rem",
                                        borderRadius: "4px",
                                        height: "24px",
                                        "& .MuiChip-label": { px: 1 },
                                        "&:hover": { bgcolor: `${accentColor}10` }
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                )}

                {/* 3. Professional Experience */}
                {displayedProjects?.length > 0 && (
                    <Box mb={3}>
                        <Typography variant="subtitle2" sx={{ color: accentColor, fontWeight: 800, mb: 2, textTransform: "uppercase", letterSpacing: 1.5, borderBottom: `1px solid ${accentColor}30`, pb: 0.5 }}>
                            Experience
                        </Typography>

                        <Stack spacing={3}>
                            {displayedProjects.map((exp, i) => (
                                <Box key={i} sx={{
                                    position: "relative",
                                    textAlign: forPDF ? "left" : "justify",
                                    "&:hover .editIcon": {
                                        visibility: "visible",
                                    },
                                }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.5 }}>
                                        <Box>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Typography sx={{ fontWeight: 800, color: "#1a1a1a", fontSize: "0.95rem" }}>
                                                    {exp.pro_title || "Project Title"}
                                                </Typography>
                                                {currentSection?.section === "Project" && (
                                                    <IconButton
                                                        className="editIcon"
                                                        size="small"
                                                        sx={{
                                                            visibility: "hidden",
                                                            p: 0,
                                                            color: accentColor
                                                        }}
                                                        onClick={() => handleEditClick("Project", exp.id)}
                                                    >
                                                        <Edit3 size={14} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                            <Typography sx={{ color: accentColor, fontWeight: 700, fontSize: "0.85rem" }}>
                                                {exp.pro_client && toTitleCase(exp.pro_client)}
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontWeight: 700, color: accentColor, fontSize: "0.75rem", bgcolor: `${accentColor}10`, px: 1, py: 0.2, borderRadius: "4px" }}>
                                            {exp.pro_startdate && formatDate(exp.pro_startdate)} – {exp.is_current_project === 1 ? "Present" : (exp.pro_enddate && formatDate(exp.pro_enddate))}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ pl: 2, borderLeft: `2px solid ${accentColor}20` }}>
                                        <Typography sx={{ color: "text.secondary", fontSize: "0.8rem", mb: 0.5, fontStyle: "italic" }}>
                                            {exp.pro_role && toTitleCase(exp.pro_role)} • {[exp.cityname, exp.statename].filter(Boolean).join(", ")}
                                        </Typography>
                                        {exp.pro_description && (
                                            <Typography sx={{ color: "#555", mb: 1, lineHeight: 1.5, fontSize: "0.85rem", textAlign: forPDF ? "left" : "justify" }}>
                                                {parse(exp.pro_description)}
                                            </Typography>
                                        )}
                                        {exp.pro_responsibilities && (
                                            <Typography sx={{ color: "#444", textAlign: forPDF ? "left" : "justify", lineHeight: 1.5, fontSize: "0.85rem" }}>
                                                {parse(exp.pro_responsibilities)}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                )}

                <Grid container spacing={4}>
                    {/* Education */}
                    {educationdetails?.filter(edu =>
                        (edu.edu_course && edu.edu_course !== "null" && edu.edu_course !== "undefined" && edu.edu_course.trim() !== "") ||
                        (edu.edu_university && edu.edu_university !== "null" && edu.edu_university !== "undefined" && edu.edu_university.trim() !== "")
                    ).length > 0 && (
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" sx={{ color: accentColor, fontWeight: 800, mb: 2, textTransform: "uppercase", letterSpacing: 1.5, borderBottom: `1px solid ${accentColor}30`, pb: 0.5 }}>
                                    Education
                                </Typography>
                                <Stack spacing={2}>
                                    {educationdetails
                                        .filter(edu =>
                                            (edu.edu_course && edu.edu_course !== "null" && edu.edu_course !== "undefined" && edu.edu_course.trim() !== "") ||
                                            (edu.edu_university && edu.edu_university !== "null" && edu.edu_university !== "undefined" && edu.edu_university.trim() !== "")
                                        )
                                        .map((edu, i) => (
                                            <Box key={i}>
                                                {edu.edu_course && edu.edu_course !== "null" && edu.edu_course !== "undefined" && (
                                                    <Typography sx={{ fontWeight: 800, fontSize: "0.85rem" }}>{toTitleCase(edu.edu_course)}</Typography>
                                                )}
                                                {edu.edu_university && edu.edu_university !== "null" && edu.edu_university !== "undefined" && (
                                                    <Typography sx={{ color: accentColor, fontWeight: 600, mt: 0.2, fontSize: "0.8rem" }}>{toTitleCase(edu.edu_university)}</Typography>
                                                )}
                                                {edu.edu_passing_year && edu.edu_passing_year !== "null" && edu.edu_passing_year !== "undefined" && (
                                                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.2, fontSize: "0.75rem" }}>{edu.edu_passing_year}</Typography>
                                                )}
                                            </Box>
                                        ))}
                                </Stack>
                            </Grid>
                        )}

                    {/* Certifications */}
                    {certificatedetails?.filter(cert =>
                        cert.cer_name &&
                        cert.cer_name !== "null" &&
                        cert.cer_name !== "undefined" &&
                        cert.cer_name.trim() !== ""
                    ).length > 0 && (
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" sx={{ color: accentColor, fontWeight: 800, mb: 2, textTransform: "uppercase", letterSpacing: 1.5, borderBottom: `1px solid ${accentColor}30`, pb: 0.5 }}>
                                    Certifications
                                </Typography>
                                <Stack spacing={1.5}>
                                    {certificatedetails
                                        .filter(cert =>
                                            cert.cer_name &&
                                            cert.cer_name !== "null" &&
                                            cert.cer_name !== "undefined" &&
                                            cert.cer_name.trim() !== ""
                                        )
                                        .map((cert, i) => (
                                            <Stack key={i} direction="row" spacing={1} alignItems="center">
                                                <Award size={16} color={accentColor} />
                                                <Box>
                                                    <Typography sx={{ fontWeight: 800, fontSize: "0.85rem" }}>{toTitleCase(cert.cer_name)}</Typography>
                                                    {cert.cer_year && cert.cer_year !== "null" && cert.cer_year !== "undefined" && (
                                                        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>{cert.cer_year}</Typography>
                                                    )}
                                                </Box>
                                            </Stack>
                                        ))}
                                </Stack>
                            </Grid>
                        )}
                </Grid>
            </Box>
        </Box>
    );
};

export default Template3;
